import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Checkbox, Typography, ConfigProvider } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, ArrowRightOutlined, GithubOutlined, GoogleOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import FireflyField from '../../components/FireflyField';
import { useAuthStore } from '../../stores/auth';

const { Text, Title } = Typography;

/* ═══════════════════════════════════════════
   Simon Cat Agent ｜萤火秘境 · 登录页
   ═══════════════════════════════════════════ */

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [rememberMe, setRememberMe] = useState(false);
  const loginAction = useAuthStore((s) => s.login);
  const registerAction = useAuthStore((s) => s.register);
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (activeTab === 'login') {
        await loginAction({
          email: values.email,
          password: values.password,
          loginType: 'PASSWORD',
        });
      } else {
        await registerAction({
          email: values.email,
          password: values.password,
          nickname: values.username || values.email?.split('@')[0],
        });
      }
      if (rememberMe) {
        localStorage.setItem('simon-cat-auth', JSON.stringify({
          state: { token: 'auth-token', user: { nickname: values.username || values.email?.split('@')[0] || 'U' } }
        }));
      }
      navigate('/');
    } catch (err) {
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate('/auth/forgot');
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      position: 'relative',
      overflow: 'hidden',
      padding: '0',
    }}>
      {/* ═══ Layer 1: 神鹿湖畔背景 ═══ */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'url(/hero-deer-forest.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        zIndex: 0,
      }} />

      {/* ═══ Layer 2: 蓝色夜色蒙版 — 非常轻 ═══ */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(90deg, rgba(4,15,34,0.38) 0%, rgba(4,15,34,0.22) 40%, rgba(4,15,34,0.08) 70%, transparent 100%)',
        zIndex: 1,
      }} />

      {/* ═══ Layer 3: 底部沉降 ═══ */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to bottom, transparent 60%, rgba(7,21,45,0.15) 80%, rgba(7,21,45,0.35) 100%)',
        zIndex: 1,
      }} />

      {/* ═══ Layer 4: 萤火虫 ═══ */}
      <FireflyField count={7} area="default" />

      {/* ═══ 登录面板 — 左侧 Desktop / 居中 Mobile ═══ */}
      <div
        className="login-panel"
        style={{
          position: 'relative',
          zIndex: 10,
          width: 440,
          maxWidth: '90vw',
          marginLeft: '8vw',
          padding: '44px 40px',
          background: 'rgba(8, 22, 40, 0.62)',
          borderRadius: 24,
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          border: '1px solid rgba(220,235,255,0.12)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img
            src="/logo-cat-white.png"
            alt="Simon Cat Agent"
            style={{
              width: 40,
              height: 40,
              filter: 'drop-shadow(0 0 12px rgba(220,238,255,0.12))',
            }}
          />
          <Title level={4} style={{
            color: 'var(--text-primary)',
            margin: '16px 0 4px',
            fontWeight: 400,
            fontSize: 20,
            letterSpacing: '0.02em',
            fontFamily: 'var(--font-serif)',
          }}>
            回到你的创作空间
          </Title>
          <Text style={{
            color: 'var(--text-secondary)',
            fontSize: 13,
            fontFamily: 'var(--font-sans)',
          }}>
            AI 陪伴创作，让灵感发光
          </Text>
        </div>

        {/* Tab 导航 */}
        <div style={{
          display: 'flex',
          gap: 24,
          marginBottom: 28,
          borderBottom: '1px solid rgba(220,235,255,0.06)',
        }}>
          <button
            onClick={() => { setActiveTab('login'); form.resetFields(); }}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '0 4px 12px',
              fontSize: 15,
              fontFamily: 'var(--font-sans)',
              cursor: 'pointer',
              color: activeTab === 'login' ? '#E9D38A' : 'rgba(220,235,255,0.58)',
              borderBottom: activeTab === 'login' ? '2px solid #E9D38A' : '2px solid transparent',
              transition: 'color 200ms, border-color 200ms',
              fontWeight: activeTab === 'login' ? 500 : 400,
            }}
          >
            登录
          </button>
          <button
            onClick={() => { setActiveTab('register'); form.resetFields(); }}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '0 4px 12px',
              fontSize: 15,
              fontFamily: 'var(--font-sans)',
              cursor: 'pointer',
              color: activeTab === 'register' ? '#E9D38A' : 'rgba(220,235,255,0.58)',
              borderBottom: activeTab === 'register' ? '2px solid #E9D38A' : '2px solid transparent',
              transition: 'color 200ms, border-color 200ms',
              fontWeight: activeTab === 'register' ? 500 : 400,
            }}
          >
            注册
          </button>
        </div>

        {/* 表单 */}
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#E9D38A',
              colorBgContainer: 'rgba(255,255,255,0.045)',
            },
          }}
        >
          <Form
            form={form}
            onFinish={onFinish}
            size="large"
            style={{ width: '100%' }}
          >
            {/* 注册时显示用户名 */}
            {activeTab === 'register' && (
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入用户名' }]}
                style={{ marginBottom: 16 }}
              >
                <Input
                  prefix={<UserOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
                  placeholder="用户名"
                  style={{
                    height: 52,
                    background: 'rgba(255,255,255,0.045)',
                    border: '1px solid rgba(220,235,255,0.10)',
                    borderRadius: 14,
                    color: 'var(--text-primary)',
                    fontSize: 14,
                    fontFamily: 'var(--font-sans)',
                  }}
                />
              </Form.Item>
            )}

            {/* 邮箱 */}
            <Form.Item
              name="email"
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '邮箱格式不正确' },
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input
                prefix={<MailOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
                placeholder="输入邮箱"
                style={{
                  height: 52,
                  background: 'rgba(255,255,255,0.045)',
                  border: '1px solid rgba(220,235,255,0.10)',
                  borderRadius: 14,
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  fontFamily: 'var(--font-sans)',
                }}
              />
            </Form.Item>

            {/* 密码 */}
            <Form.Item
              name="password"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6位' },
              ]}
              style={{ marginBottom: 16 }}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
                placeholder="输入密码"
                iconRender={(visible) => (
                  visible
                    ? <EyeOutlined style={{ color: 'rgba(220,235,255,0.65)' }} />
                    : <EyeInvisibleOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />
                )}
                style={{
                  height: 52,
                  background: 'rgba(255,255,255,0.045)',
                  border: '1px solid rgba(220,235,255,0.10)',
                  borderRadius: 14,
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  fontFamily: 'var(--font-sans)',
                }}
              />
            </Form.Item>

            {/* 注册时的确认密码 */}
            {activeTab === 'register' && (
              <Form.Item
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: '请确认密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
                style={{ marginBottom: 16 }}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: 'rgba(220,235,255,0.42)' }} />}
                  placeholder="确认密码"
                  style={{
                    height: 52,
                    background: 'rgba(255,255,255,0.045)',
                    border: '1px solid rgba(220,235,255,0.10)',
                    borderRadius: 14,
                    color: 'var(--text-primary)',
                    fontSize: 14,
                    fontFamily: 'var(--font-sans)',
                  }}
                />
              </Form.Item>
            )}

            {/* 记住我 & 忘记密码 — 登录时 */}
            {activeTab === 'login' && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
              }}>
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{
                    color: 'rgba(220,235,255,0.58)',
                    fontSize: 13,
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  记住我
                </Checkbox>
                <a
                  onClick={handleForgotPassword}
                  style={{
                    color: '#E9D38A',
                    fontSize: 13,
                    fontFamily: 'var(--font-sans)',
                    cursor: 'pointer',
                    opacity: 0.85,
                    transition: 'opacity 150ms',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                >
                  忘记密码
                </a>
              </div>
            )}

            {/* 用户协议 — 注册时 */}
            {activeTab === 'register' && (
              <div style={{ marginBottom: 20, fontSize: 13, color: 'rgba(220,235,255,0.58)', fontFamily: 'var(--font-sans)' }}>
                我已阅读并同意 <span style={{ color: '#E9D38A', opacity: 0.85 }}>《用户服务协议》</span> 和 <span style={{ color: '#E9D38A', opacity: 0.85 }}>《隐私政策》</span>
              </div>
            )}

            {/* 主按钮 */}
            <Form.Item style={{ marginBottom: 20 }}>
              <Button
                type="primary"
                block
                loading={loading}
                htmlType="submit"
                style={{
                  height: 52,
                  background: 'linear-gradient(135deg, #F2D87E 0%, #DDB94F 100%)',
                  border: 'none',
                  borderRadius: 14,
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#08162A',
                  fontFamily: 'var(--font-sans)',
                  boxShadow: '0 4px 20px rgba(235,216,126,0.18)',
                  transition: 'transform 180ms, filter 180ms',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'brightness(1.04)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'brightness(1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {activeTab === 'login' ? '进入我的创作空间' : '加入萤火秘境'} <ArrowRightOutlined style={{ fontSize: 12, marginLeft: 4 }} />
              </Button>
            </Form.Item>

            {/* 分隔线 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              margin: '20px 0',
            }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(220,235,255,0.08)' }} />
              <span style={{
                color: 'rgba(220,235,255,0.30)',
                fontSize: 12,
                fontFamily: 'var(--font-sans)',
                whiteSpace: 'nowrap',
              }}>
                或使用
              </span>
              <div style={{ flex: 1, height: 1, background: 'rgba(220,235,255,0.08)' }} />
            </div>

            {/* Social Login */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                style={{
                  flex: 1,
                  height: 46,
                  background: 'rgba(255,255,255,0.035)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 14,
                  color: 'rgba(220,235,255,0.65)',
                  fontSize: 18,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 150ms, border-color 150ms',
                  fontFamily: 'var(--font-sans)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.035)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)';
                }}
              >
                <GithubOutlined />
              </button>
              <button
                type="button"
                style={{
                  flex: 1,
                  height: 46,
                  background: 'rgba(255,255,255,0.035)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 14,
                  color: 'rgba(220,235,255,0.65)',
                  fontSize: 18,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 150ms, border-color 150ms',
                  fontFamily: 'var(--font-sans)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.035)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)';
                }}
              >
                <GoogleOutlined />
              </button>
            </div>
          </Form>
        </ConfigProvider>

        {/* 版权 */}
        <div style={{
          textAlign: 'center',
          marginTop: 28,
          color: 'rgba(220,235,255,0.30)',
          fontSize: 12,
          fontFamily: 'var(--font-sans)',
        }}>
          © 2026 西蒙的猫 · 萤火秘境
        </div>
      </div>

      {/* ═══ 全局输入框样式覆盖 ═══ */}
      <style>{`
        .ant-input,
        .ant-input-password,
        .ant-input-affix-wrapper {
          background: rgba(255,255,255,0.045) !important;
          border-color: rgba(220,235,255,0.10) !important;
          color: var(--text-primary) !important;
          font-family: var(--font-sans) !important;
        }
        .ant-input-password input,
        .ant-input-affix-wrapper input {
          background: transparent !important;
          color: var(--text-primary) !important;
        }
        .ant-input::placeholder,
        .ant-input-password input::placeholder {
          color: rgba(220,235,255,0.38) !important;
        }
        .ant-input:hover,
        .ant-input-password:hover,
        .ant-input-affix-wrapper:hover {
          border-color: rgba(220,235,255,0.18) !important;
        }
        .ant-input:focus,
        .ant-input-focused,
        .ant-input-password:focus-within,
        .ant-input-affix-wrapper-focused {
          border-color: rgba(235,220,155,0.45) !important;
          box-shadow: 0 0 0 3px rgba(235,220,155,0.06) !important;
        }
        .ant-input-prefix {
          margin-right: 10px !important;
        }
        .ant-checkbox-inner {
          background: rgba(255,255,255,0.05) !important;
          border-color: rgba(220,235,255,0.2) !important;
        }
        .ant-checkbox-checked .ant-checkbox-inner {
          background: #E9D38A !important;
          border-color: #E9D38A !important;
        }
        .ant-checkbox-checked .ant-checkbox-inner::after {
          border-color: #08162A !important;
        }
        .ant-form-item-explain-error {
          color: rgba(255,140,120,0.85) !important;
          font-size: 12px !important;
          margin-top: 4px !important;
        }
        .ant-input-show-count-suffix {
          color: rgba(220,235,255,0.3) !important;
        }
        @media (max-width: 768px) {
          .login-panel {
            margin-left: auto !important;
            margin-right: auto !important;
            width: 100% !important;
            max-width: 440px !important;
            border-radius: 20px !important;
            padding: 36px 24px !important;
          }
        }
        @media (max-width: 480px) {
          .login-panel {
            margin-left: 12px !important;
            margin-right: 12px !important;
            width: calc(100% - 24px) !important;
            max-width: 100% !important;
            border-radius: 18px !important;
            padding: 28px 20px !important;
          }
        }
      `}</style>
    </div>
  );
}
